<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useH3liveRooms } from '../stores/h3live-rooms.js'
import { useSignupReward } from '../stores/signup-reward.js'
import { useVisualAccount } from '../stores/visual-account.js'
import SiteAuthDialog from './SiteAuthDialog.vue'
import SiteLanguageSwitcher from './SiteLanguageSwitcher.vue'
import SiteLoadingMark from './SiteLoadingMark.vue'
import SiteNotificationBell from './SiteNotificationBell.vue'
import VgIcon from './trpg/icons/VgIcon.vue'

const api = inject('api')
if (!api) throw new Error('SiteTopNav: api provider is required')

const props = defineProps({
  mobileBackHref: { type: String, default: '' },
  mobileBackHistory: { type: Boolean, default: false },
  mobileActionsLast: { type: Boolean, default: false },
  mobileDrawer: { type: Boolean, default: false },
  mobileMinimal: { type: Boolean, default: false },
  mobileTitle: { type: String, default: '' },
})

const { t } = useI18n()
const account = useVisualAccount()
const h3liveRooms = useH3liveRooms()
const signupReward = useSignupReward()
const accountSessionReady = ref(!api.tokenStore.getAccessToken() && !api.tokenStore.getRefreshToken())
const accountMenuOpen = ref(false)
const authOpen = ref(false)
const navigationPending = ref(false)
const mobileViewport = ref(globalThis.matchMedia('(max-width: 680px)').matches)
// 送积分的气泡只在页面顶部出现：顶栏吸顶，滚下去后常驻的气泡会压住页面里吸顶的筛选栏
const pageAtTop = ref(globalThis.scrollY < 24)
const currentPath = ref(globalThis.location.pathname)
const locationParams = new URLSearchParams(globalThis.location.search)
const loginTarget = ref(validSameOriginTarget(locationParams.get('returnTo')))
const mobileMediaQuery = globalThis.matchMedia('(max-width: 680px)')
let navigationTimer = 0

// 第三方登录跳回地址:优先用已算出的登录目标,否则就是当前页
const dialogReturnTo = computed(() => loginTarget.value || `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`)
const accountName = computed(() => account.account.value?.display_name || t('visualGame.account.signedIn'))
const accountInitial = computed(() => accountName.value.trim().slice(0, 1).toUpperCase())
const desktopNavItems = computed(() => [
  { href: '/', label: t('visualGame.landingCinema.navHome'), active: currentPath.value === '/' },
  { href: '/stories', label: t('visualGame.landingCinema.navDramas'), active: currentPath.value === '/stories' },
  { href: '/dramas', label: t('visualGame.landingCinema.navPlayerStories'), active: currentPath.value === '/dramas' },
  // 直播入口按服务端配置门控：embed_origin 为空（没接 h3live）就整个不渲染，不留一个点了没反应的链接。
  ...(h3liveRooms.embedOrigin.value === '' ? [] : [{ href: '/live', label: t('visualGame.landingCinema.navLive'), active: currentPath.value === '/live' || currentPath.value.startsWith('/live/') }]),
  { href: '/create', label: t('visualGame.creator.nav'), active: currentPath.value.startsWith('/create') },
])
const mobileNavItems = computed(() => [
  { href: '/', icon: 'home', label: t('visualGame.landingCinema.navHome'), active: currentPath.value === '/' },
  { href: '/stories', icon: 'gamepad', label: t('visualGame.landingCinema.navDramas'), active: currentPath.value === '/stories' },
  { href: '/dramas', icon: 'play', label: t('visualGame.landingCinema.navPlayerStories'), active: currentPath.value === '/dramas' },
  {
    href: account.isSignedIn.value ? '/create' : '/login?returnTo=%2Fcreate',
    icon: 'review',
    label: t('visualGame.creator.nav'),
    active: currentPath.value.startsWith('/create'),
  },
  {
    href: account.isSignedIn.value ? '/account' : '/login?returnTo=%2Faccount',
    icon: 'user',
    label: t('visualGame.landingCinema.navMy'),
    active: currentPath.value.startsWith('/account') || ['/my-games', '/my-videos', '/my-favorites'].includes(currentPath.value),
  },
])

onMounted(() => {
  if (locationParams.get('auth') === 'login' && mobileViewport.value) {
    globalThis.location.replace(mobileLoginHref(loginTarget.value, locationParams.get('email') || ''))
    return
  }
  restoreAccountSession()
  if (locationParams.get('auth') === 'login' || locationParams.get('auth') === 'register') {
    authOpen.value = true
  }
  if (globalThis.location.pathname === '/invite' || globalThis.location.pathname.includes('/invite/')) {
    authOpen.value = true
  }
  syncCurrentPath()
  globalThis.addEventListener('popstate', syncCurrentPath)
  globalThis.addEventListener('scroll', syncPageAtTop, { passive: true })
  mobileMediaQuery.addEventListener('change', syncMobileViewport)
})

onBeforeUnmount(() => {
  globalThis.removeEventListener('popstate', syncCurrentPath)
  globalThis.removeEventListener('scroll', syncPageAtTop)
  mobileMediaQuery.removeEventListener('change', syncMobileViewport)
  globalThis.clearTimeout(navigationTimer)
})

function syncCurrentPath() {
  currentPath.value = globalThis.location.pathname
}

function syncMobileViewport(event) {
  mobileViewport.value = event.matches
}

function syncPageAtTop() {
  pageAtTop.value = globalThis.scrollY < 24
}

function navigateFromTab(event, item) {
  if (!mobileViewport.value || (item.active && item.href === currentPath.value)) return
  event.preventDefault()
  navigationPending.value = true
  navigationTimer = globalThis.setTimeout(() => {
    const target = new URL(item.href, globalThis.location.origin)
    const samePage = target.pathname === globalThis.location.pathname
    globalThis.history.pushState({}, '', target)
    if (samePage && target.searchParams.get('auth') === 'login') {
      loginTarget.value = validSameOriginTarget(target.searchParams.get('returnTo'))
      authOpen.value = true
      navigationPending.value = false
      return
    }
    globalThis.dispatchEvent(new Event('visual-game-route-change'))
  }, 120)
}

async function restoreAccountSession() {
  await account.load()
  accountSessionReady.value = true
  accountMenuOpen.value = false
}

async function logout() {
  const result = await account.logout()
  if (!result.ok) return
  accountMenuOpen.value = false
}

function openLogin(target = '') {
  loginTarget.value = validSameOriginTarget(target)
  if (mobileViewport.value) {
    globalThis.location.assign(mobileLoginHref(loginTarget.value))
    return
  }
  authOpen.value = true
}

function mobileLoginHref(target, email = '') {
  const params = new URLSearchParams()
  if (target) params.set('returnTo', target)
  if (email) params.set('email', email)
  const query = params.toString()
  return query ? `/login?${query}` : '/login'
}

async function handleSignedIn() {
  await account.load()
  accountSessionReady.value = true
  accountMenuOpen.value = false
  const target = loginTarget.value
  const url = new URL(globalThis.location.href)
  url.searchParams.delete('auth')
  url.searchParams.delete('email')
  url.searchParams.delete('returnTo')
  globalThis.history.replaceState(globalThis.history.state, '', `${url.pathname}${url.search}${url.hash}`)
  if (target) globalThis.location.assign(target)
}

function validSameOriginTarget(value) {
  if (!value) return ''
  const target = new URL(value, globalThis.location.origin)
  if (target.origin !== globalThis.location.origin) return ''
  return `${target.pathname}${target.search}${target.hash}`
}

function navigateMobileBack(event) {
  if (!props.mobileBackHistory) return
  event.preventDefault()
  const referrer = document.referrer
  if (referrer !== '' && new URL(referrer).origin === globalThis.location.origin) {
    globalThis.history.back()
    return
  }
  globalThis.location.assign(props.mobileBackHref)
}

defineExpose({ openLogin })
</script>

<template>
  <header class="site-top-nav" :class="{ 'has-mobile-drawer': mobileDrawer, 'has-mobile-title': mobileTitle, 'has-mobile-actions-last': mobileActionsLast, 'is-mobile-minimal': mobileMinimal }">
    <a class="site-top-nav-brand" href="/" :aria-label="t('visualGame.landing.homeLabel')">
      <img src="/assets/brand/voko-logo.webp" alt="" aria-hidden="true">
      <span class="site-top-nav-wordmark"><strong>voko</strong><em>The Playable Drama</em></span>
    </a>
    <a v-if="mobileBackHref" class="site-top-nav-mobile-back" :href="mobileBackHref" :aria-label="t('common.back')" @click="navigateMobileBack">
      <VgIcon name="back" :size="20" />
    </a>
    <strong v-if="mobileTitle" class="site-top-nav-mobile-title">{{ mobileTitle }}</strong>
    <div v-if="!mobileActionsLast" class="site-top-nav-mobile-actions"><slot name="mobile-actions"></slot></div>
    <SiteNotificationBell
      v-if="!mobileActionsLast && mobileDrawer && mobileViewport && accountSessionReady && account.isSignedIn.value"
      class="site-top-nav-mobile-notification"
      inline
    />
    <a class="site-top-nav-mobile-search" href="/search" :aria-label="t('visualGame.landing.searchLabel')">
      <VgIcon name="search" :size="20" />
    </a>
    <SiteNotificationBell
      v-if="mobileActionsLast && mobileDrawer && mobileViewport && accountSessionReady && account.isSignedIn.value"
      class="site-top-nav-mobile-notification"
      inline
    />
    <div v-if="mobileActionsLast" class="site-top-nav-mobile-actions"><slot name="mobile-actions"></slot></div>
    <div id="site-top-nav-drawer" class="site-top-nav-drawer">
      <nav class="site-top-nav-menu site-top-nav-menu-desktop" :aria-label="t('visualGame.landing.navLabel')">
        <a
          v-for="item in desktopNavItems"
          :key="item.href"
          :href="item.href"
          :aria-current="item.active ? 'page' : undefined"
          :class="{ 'is-active': item.active }"
        >
          {{ item.label }}
        </a>
      </nav>
      <nav class="site-top-nav-menu site-top-nav-menu-mobile" :aria-label="t('visualGame.landing.navLabel')">
        <a
          v-for="item in mobileNavItems"
          :key="item.href"
          :href="item.href"
          :aria-current="item.active ? 'page' : undefined"
          :class="{ 'is-active': item.active }"
          @click="navigateFromTab($event, item)"
        >
          <i aria-hidden="true"><VgIcon :name="item.icon" :size="21" /></i>
          <span>{{ item.label }}</span>
        </a>
      </nav>
      <div class="site-top-nav-actions">
        <a class="site-top-nav-search" href="/search" :aria-label="t('visualGame.landing.searchLabel')">
          <VgIcon name="search" :size="18" />
        </a>
        <SiteLanguageSwitcher mobile-list />
        <span v-if="!accountSessionReady" class="account-session-loading" aria-hidden="true"></span>
        <template v-else-if="account.isSignedIn.value">
          <SiteNotificationBell v-if="!mobileViewport" inline />
          <a
            v-if="!mobileViewport"
            class="account-credit-link"
            href="/billing"
            :aria-label="`${t('visualGame.billing.balance')}：${account.credits.value} ${t('visualGame.account.credits')}`"
          >
            <VgIcon name="coin" :size="17" />
            <strong>{{ account.credits.value }}</strong>
            <span>{{ t('visualGame.account.credits') }}</span>
          </a>
          <div v-if="mobileViewport" class="mobile-account-footer">
            <span class="mobile-account-summary">
              <i>{{ accountInitial }}</i>
              <strong>{{ accountName }}</strong>
            </span>
            <a class="mobile-account-balance" href="/billing">
              <VgIcon name="coin" :size="18" />
              <strong>{{ account.credits.value }}</strong>
              <span>{{ t('visualGame.account.credits') }}</span>
            </a>
            <a href="/account">{{ t('visualGame.accountCenter.title') }}</a>
            <button type="button" :disabled="account.authBusy.value" @click="logout">
              {{ t('visualGame.account.logout') }}
            </button>
          </div>
          <template v-else>
            <button
              class="account-trigger"
              type="button"
              :aria-expanded="accountMenuOpen"
              @click="accountMenuOpen = !accountMenuOpen"
            >
              <span>{{ accountInitial }}</span>
              <strong>{{ accountName }}</strong>
              <i aria-hidden="true"></i>
            </button>
            <div v-if="accountMenuOpen" class="account-menu">
              <a href="/account">{{ t('visualGame.accountCenter.title') }}</a>
              <button type="button" :disabled="account.authBusy.value" @click="logout">
                {{ t('visualGame.account.logout') }}
              </button>
            </div>
          </template>
        </template>
        <span v-else class="nav-signup">
          <button class="nav-cta nav-action" type="button" data-testid="nav-signup" @click="openLogin()">{{ t('visualGame.account.loginOrSignUp') }}</button>
          <Transition name="nav-signup-reward">
            <button v-if="signupReward.points > 0 && pageAtTop" class="nav-signup-reward" type="button" data-testid="nav-signup-reward" @click="openLogin()">
              <VgIcon name="coin" :size="15" />
              <span>{{ t('visualGame.authDialog.signupReward', { points: signupReward.points }) }}</span>
            </button>
          </Transition>
        </span>
      </div>
    </div>
    <Transition name="route-loading">
      <div v-if="navigationPending" class="site-route-loading" role="status" :aria-label="t('visualGame.status.loading')">
        <SiteLoadingMark />
      </div>
    </Transition>
    <SiteAuthDialog
      v-model:open="authOpen"
      :account="account"
      :initial-email="locationParams.get('email') || ''"
      :return-to="dialogReturnTo"
      @signed-in="handleSignedIn"
    />
  </header>
</template>

<style scoped>
.site-top-nav { position: sticky; z-index: 30; top: 0; display: flex; width: 100%; min-height: 56px; align-items: center; justify-content: space-between; gap: clamp(var(--drama-space-lg), 2.4vw, 36px); padding: 6px clamp(20px, 3vw, 40px); border-bottom: 1px solid var(--drama-color-border); background: var(--drama-color-surface); }
.site-top-nav-brand { display: inline-flex; align-items: center; gap: 10px; color: var(--drama-color-text); text-decoration: none; }
.site-top-nav-brand img { display: block; width: 40px; height: 40px; border-radius: var(--drama-radius-sm); object-fit: contain; }
.site-top-nav-wordmark { display: inline-flex; align-items: baseline; font-family: var(--vgs-font-serif); font-size: 19px; letter-spacing: -.035em; line-height: 1; white-space: nowrap; }
.site-top-nav-wordmark strong { color: var(--drama-color-text); font-weight: 500; }
.site-top-nav-wordmark em { color: var(--drama-color-text-muted); font-family: var(--drama-font-family-body); font-size: 13px; font-style: normal; font-weight: var(--drama-font-weight-medium); letter-spacing: 0; }
.site-top-nav-wordmark em::before { margin: 0 .45em; content: '·'; }
.site-top-nav-mobile-title { display: none; }
.site-top-nav-mobile-back { display: none; }
.site-top-nav-mobile-actions { display: none; }
.site-top-nav-drawer { display: flex; min-width: 0; flex: 1 1 auto; align-items: center; justify-content: space-between; gap: clamp(var(--drama-space-xl), 3vw, 44px); }
.site-top-nav-toggle, .site-top-nav-close, .site-top-nav-scrim { display: none; }
.site-top-nav-mobile-search { display: none; }
.site-top-nav-search { display: grid; width: 36px; height: 36px; place-items: center; border-radius: var(--drama-radius-full); color: var(--drama-color-text-muted); text-decoration: none; }
.site-top-nav-search:hover, .site-top-nav-search:focus-visible { background: var(--drama-color-surface-alt); color: var(--drama-color-text); }
.site-route-loading { position: fixed; z-index: 29; inset: 0; display: grid; place-items: center; background: color-mix(in srgb, var(--drama-color-bg) 92%, transparent); backdrop-filter: blur(12px); }
.route-loading-enter-active { transition: opacity .12s ease; }
.route-loading-enter-from { opacity: 0; }
.site-top-nav-menu { position: absolute; left: 50%; display: flex; align-items: center; gap: 2px; padding: 2px; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); transform: translateX(-50%); }
.site-top-nav-menu-mobile { display: none; }
.site-top-nav-menu a { display: inline-flex; min-height: 32px; align-items: center; justify-content: center; padding: 0 12px; border-radius: var(--drama-radius-full); color: var(--drama-color-text-muted); font-size: 13px; font-weight: var(--drama-font-weight-medium); letter-spacing: 0; text-decoration: none; transition: color .2s ease, background .2s ease; }
.site-top-nav-menu a:hover, .site-top-nav-menu a:focus-visible { color: var(--drama-color-text); }
.site-top-nav-menu a.is-active { background: var(--drama-color-primary); color: var(--drama-color-on-primary); font-weight: var(--drama-font-weight-semibold); }
.site-top-nav-actions { position: relative; display: flex; align-items: center; gap: var(--drama-space-sm); margin-left: auto; }
.account-session-loading { width: 124px; height: 42px; border: 1px solid var(--drama-color-border); background: color-mix(in srgb, var(--drama-color-surface) 44%, transparent); }
.account-credit-link { display: inline-flex; min-height: 36px; align-items: center; gap: var(--drama-space-xs); padding: 0 var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); color: var(--drama-color-text); text-decoration: none; transition: border-color .2s ease, background .2s ease; }
.account-credit-link:hover,
.account-credit-link:focus-visible { border-color: var(--drama-color-border-strong); background: var(--drama-color-surface); }
.account-credit-link strong { font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-sm); font-weight: var(--drama-font-weight-bold); }
.account-credit-link span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.nav-cta { display: inline-flex; min-height: 38px; align-items: center; justify-content: center; padding: 0 var(--drama-space-md); border: 1px solid transparent; border-radius: var(--drama-radius-sm); color: var(--drama-color-text); font-family: inherit; font-size: 12px; font-weight: var(--drama-font-weight-medium); letter-spacing: .03em; text-decoration: none; cursor: pointer; }
.nav-action { min-width: 112px; border-color: var(--drama-color-primary); background: var(--drama-color-primary); color: var(--drama-color-on-primary); white-space: nowrap; transition: background .2s ease, box-shadow .2s ease; }
.nav-action:hover,
.nav-action:focus-visible { background: var(--drama-color-primary-hover); box-shadow: 0 6px 18px color-mix(in srgb, var(--drama-color-primary) 32%, transparent); }
/* 注册奖励气泡挂在注册按钮正下方，尖角指着按钮；点它和点按钮一样打开注册 */
.nav-signup { position: relative; display: inline-flex; }
.nav-signup-reward { position: absolute; z-index: 1; top: calc(100% + 12px); right: 0; display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); color: var(--drama-color-text); font-family: inherit; font-size: 12px; font-weight: var(--drama-font-weight-semibold); white-space: nowrap; cursor: pointer; }
.nav-signup-reward::before { position: absolute; top: -6px; right: 55px; width: 10px; height: 10px; border-top: 1px solid var(--drama-color-border); border-left: 1px solid var(--drama-color-border); background: var(--drama-color-surface); content: ''; transform: rotate(45deg); }
.nav-signup-reward :deep(.vg-icon) { color: var(--drama-color-warning); }
.nav-signup-reward-enter-active,
.nav-signup-reward-leave-active { transition: opacity .2s ease, transform .2s ease; }
.nav-signup-reward-enter-from,
.nav-signup-reward-leave-to { opacity: 0; transform: translateY(-4px); }
.account-trigger { display: flex; min-height: 38px; align-items: center; gap: 8px; padding: 4px 10px 4px 5px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); color: var(--drama-color-text); cursor: pointer; transition: border-color .2s ease, background .2s ease; }
.account-trigger:hover,
.account-trigger:focus-visible,
.account-trigger[aria-expanded='true'] { border-color: var(--drama-color-text-muted); background: var(--drama-color-surface-alt); }
.account-trigger > span { display: grid; width: 28px; height: 28px; place-items: center; border-radius: var(--drama-radius-full); background: var(--drama-color-primary); color: var(--drama-color-on-primary); font-size: 11px; font-weight: var(--drama-font-weight-semibold); }
.account-trigger strong { max-width: 120px; overflow: hidden; font-size: 12px; font-weight: var(--drama-font-weight-medium); text-overflow: ellipsis; white-space: nowrap; }
.account-trigger i { width: 6px; height: 6px; margin: -3px 1px 0 3px; border-right: 1px solid currentColor; border-bottom: 1px solid currentColor; opacity: .58; transform: rotate(45deg); transition: transform .2s ease; }
.account-trigger[aria-expanded='true'] i { margin-top: 3px; transform: rotate(225deg); }
.account-menu { position: absolute; top: calc(100% + 5px); right: 0; display: grid; width: 184px; padding: 4px 0; border: 1px solid var(--drama-color-border); border-radius: 8px; background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); }
.account-menu a, .account-menu button { display: flex; min-height: 34px; align-items: center; padding: 0 12px; border: 0; background: transparent; color: var(--drama-color-text); font-size: 12px; font-weight: var(--drama-font-weight-regular); text-align: left; text-decoration: none; cursor: pointer; }
.account-menu a:hover, .account-menu button:hover { background: var(--drama-color-surface-alt); }
.account-menu button { border-top: 1px solid var(--drama-color-border); }
@media (max-width: 680px) {
  .site-top-nav { position: sticky; z-index: 30; top: 0; min-height: 52px; gap: 10px; padding: 6px 12px; background: color-mix(in srgb, var(--drama-color-bg) 98%, var(--drama-color-surface)); }
  .site-top-nav-drawer { min-width: 0; flex: 1 1 auto; gap: var(--drama-space-md); overflow: hidden; }
  .site-top-nav-menu { position: static; overflow-x: auto; transform: none; scrollbar-width: none; }
  .site-top-nav-menu::-webkit-scrollbar { display: none; }
  .site-top-nav-menu a { min-height: 38px; padding: 0 var(--drama-space-sm); font-size: 11px; letter-spacing: .06em; }
  .site-top-nav-actions { gap: var(--drama-space-sm); }
  .site-top-nav-brand img { width: 36px; height: 36px; }
  .site-top-nav-wordmark { display: none; }
  .site-top-nav-mobile-title { display: block; max-width: calc(100% - 52px); overflow: hidden; margin-right: auto; color: var(--drama-color-text); font-family: Inter, var(--drama-font-family-body), sans-serif; font-size: 18px; font-weight: 500; letter-spacing: normal; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; pointer-events: none; }
  .site-top-nav-mobile-back { display: grid; width: 20px; height: 36px; flex: 0 0 20px; place-items: center start; color: var(--drama-color-text); text-decoration: none; }
  .site-top-nav-mobile-actions { display: flex; flex: 0 0 auto; align-items: center; }
  .site-top-nav-mobile-actions:empty { display: none; }
  .site-top-nav-mobile-back:focus-visible { outline: 2px solid var(--drama-color-primary); outline-offset: 2px; }
  .site-top-nav.has-mobile-title { padding-inline: var(--drama-space-lg); }
  .site-top-nav.has-mobile-title .site-top-nav-mobile-search { margin-left: 0; }
  .site-top-nav.has-mobile-title .site-top-nav-brand { display: none; }
  .account-trigger strong { display: none; }
  .nav-cta { padding: 10px; letter-spacing: 0; }
  .nav-signup-reward { display: none; }
  .nav-apply { display: none; }
  .site-top-nav.has-mobile-drawer { justify-content: flex-start; gap: .25rem; }
  .has-mobile-drawer .site-top-nav-toggle { display: grid; width: 36px; height: 36px; flex: 0 0 auto; place-content: center; gap: 4px; padding: 0; border: 0; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); color: var(--drama-color-text); cursor: pointer; }
  .has-mobile-drawer .site-top-nav-toggle span { display: block; width: 16px; height: 1px; background: currentColor; }
  .has-mobile-drawer .site-top-nav-mobile-search { display: grid; width: 36px; height: 36px; flex: 0 0 auto; place-items: center; margin-left: 0; border-radius: var(--drama-radius-full); color: var(--drama-color-text); text-decoration: none; }
  .has-mobile-drawer .site-top-nav-mobile-notification { margin-left: auto; }
  .has-mobile-drawer.has-mobile-actions-last .site-top-nav-mobile-search { margin-left: auto; }
  .has-mobile-drawer.has-mobile-actions-last .site-top-nav-mobile-notification { margin-left: 0; }
  .has-mobile-drawer .site-top-nav-mobile-notification :deep(.site-notification-trigger) { width: 36px; height: 36px; }
  .has-mobile-drawer .site-top-nav-scrim { position: fixed; z-index: 20; inset: 0; display: block; padding: 0; border: 0; background: color-mix(in srgb, var(--drama-color-bg) 66%, transparent); }
  .has-mobile-drawer .site-top-nav-drawer { position: fixed; z-index: 21; right: 0; bottom: 0; left: 0; display: flex; width: 100%; max-height: min(78dvh, 620px); flex-direction: column; align-items: stretch; justify-content: flex-start; gap: var(--drama-space-xl); overflow-y: auto; padding: 56px 20px calc(20px + env(safe-area-inset-bottom)); border-top: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-xl) var(--drama-radius-xl) 0 0; background: var(--drama-color-surface); box-shadow: var(--drama-shadow-lg); transform: translateY(100%); transition: transform .24s ease; }
  .has-mobile-drawer .site-top-nav-drawer.is-open { transform: translateY(0); }
  .has-mobile-drawer .site-top-nav-close { position: absolute; top: 10px; right: 12px; display: grid; width: 34px; height: 34px; place-items: center; padding: 0; border: 0; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); color: var(--drama-color-text); font-size: 22px; cursor: pointer; }
  .has-mobile-drawer .site-top-nav-menu { display: grid; gap: 0; overflow: visible; padding: 0; border-top: 1px solid var(--drama-color-border); border-radius: 0; background: transparent; }
  .has-mobile-drawer .site-top-nav-menu a { min-height: 48px; justify-content: flex-start; padding: 0 14px; border: 0; border-bottom: 1px solid var(--drama-color-border); border-radius: 0; color: var(--drama-color-text-muted); font-size: 14px; font-weight: var(--drama-font-weight-regular); letter-spacing: 0; }
  .has-mobile-drawer .site-top-nav-menu a::after { margin-left: auto; color: var(--drama-color-text); content: '→'; font-size: 15px; opacity: 0; transform: translateX(-4px); transition: opacity .2s ease, transform .2s ease; }
  .has-mobile-drawer .site-top-nav-menu a:hover,
  .has-mobile-drawer .site-top-nav-menu a:focus-visible { color: var(--drama-color-text); }
  .has-mobile-drawer .site-top-nav-menu a.is-active { color: var(--drama-color-on-primary); font-weight: var(--drama-font-weight-semibold); }
  .has-mobile-drawer .site-top-nav-menu a.is-active::after { color: var(--drama-color-on-primary); opacity: 1; transform: translateX(0); }
  .has-mobile-drawer .site-top-nav-actions { display: flex; width: 100%; min-height: 0; flex: 1 1 auto; align-items: stretch; flex-direction: column; gap: var(--drama-space-md); margin-left: 0; }
  .has-mobile-drawer .nav-cta { display: flex; width: 100%; min-height: 42px; align-items: center; justify-content: center; padding: 0 14px; border-radius: var(--drama-radius-full); font-size: 12px; text-align: center; }
  .has-mobile-drawer .account-trigger { width: 100%; }
  .has-mobile-drawer .account-trigger strong { display: block; }
  .has-mobile-drawer .account-menu { position: static; }
  .mobile-account-footer { display: grid; gap: 0; margin-top: auto; padding-top: var(--drama-space-lg); border-top: 1px solid var(--drama-color-border); }
  .mobile-account-summary { display: flex; min-width: 0; align-items: center; gap: 10px; padding: 0 10px 14px; }
  .mobile-account-summary i { display: grid; width: 30px; height: 30px; flex: 0 0 auto; place-items: center; border-radius: var(--drama-radius-full); background: var(--drama-color-primary); color: var(--drama-color-on-primary); font-size: 11px; font-style: normal; font-weight: var(--drama-font-weight-semibold); }
  .mobile-account-summary strong { overflow: hidden; color: var(--drama-color-text); font-size: 12px; font-weight: var(--drama-font-weight-medium); text-overflow: ellipsis; white-space: nowrap; }
  .mobile-account-balance { gap: var(--drama-space-sm); }
  .mobile-account-balance strong { font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-md); }
  .mobile-account-balance span { color: var(--drama-color-text-muted); }
  .mobile-account-footer > a,
  .mobile-account-footer > button { display: flex; min-height: 44px; align-items: center; padding: 0 10px; border: 0; border-top: 1px solid var(--drama-color-border); background: transparent; color: var(--drama-color-text); font-size: 13px; text-align: left; text-decoration: none; cursor: pointer; }
  .mobile-account-footer > a::after { margin-left: auto; content: '→'; }
  .mobile-account-footer > button { color: var(--drama-color-text-muted); }
  .site-top-nav.has-mobile-drawer { justify-content: space-between; }
  .has-mobile-drawer .site-top-nav-menu-desktop { display: none; }
  .has-mobile-drawer .site-top-nav-drawer { position: fixed; z-index: 30; top: auto; right: 0; bottom: 0; left: 0; display: block; width: 100%; max-height: none; overflow: visible; padding: 0 8px env(safe-area-inset-bottom); border-top: 1px solid var(--drama-color-border); border-radius: 0; background: color-mix(in srgb, var(--drama-color-surface) 96%, transparent); box-shadow: var(--drama-shadow-lg); transform: none; transition: none; backdrop-filter: blur(18px); }
  .has-mobile-drawer .site-top-nav-menu-mobile { display: grid; height: 60px; grid-template-columns: repeat(5, minmax(0, 1fr)); align-items: stretch; gap: 0; overflow: visible; padding: 0; border: 0; background: transparent; }
  .has-mobile-drawer .site-top-nav-menu a { position: relative; display: flex; min-width: 0; min-height: 60px; align-items: center; justify-content: center; flex-direction: column; gap: 4px; padding: 7px 3px 5px; overflow: hidden; border: 0; border-radius: 0; background: transparent; color: var(--drama-color-text-muted); font-size: 10px; font-weight: var(--drama-font-weight-medium); letter-spacing: 0; line-height: 1.15; opacity: .56; text-align: center; text-decoration: none; white-space: nowrap; transition: color .18s ease, opacity .18s ease; }
  .has-mobile-drawer .site-top-nav-menu a > i { display: grid; width: 32px; height: 26px; place-items: center; border-radius: var(--drama-radius-full); color: currentColor; font-style: normal; transition: background .18s ease, transform .18s ease; }
  .has-mobile-drawer .site-top-nav-menu a :deep(.vg-icon) { stroke-width: 1.8; }
  .has-mobile-drawer .site-top-nav-menu a span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
  .has-mobile-drawer .site-top-nav-menu a::after { position: absolute; bottom: 1px; left: 50%; display: block; width: 14px; height: 2px; border-radius: var(--drama-radius-full); background: currentColor; content: ''; opacity: 0; transform: translateX(-50%) scaleX(.5); transition: opacity .18s ease, transform .18s ease; }
  .has-mobile-drawer .site-top-nav-menu a.is-active { background: transparent; color: var(--drama-color-primary); font-weight: var(--drama-font-weight-semibold); opacity: 1; }
  .has-mobile-drawer .site-top-nav-menu a.is-active > i { background: color-mix(in srgb, var(--drama-color-primary) 10%, transparent); transform: translateY(-1px) scale(1.05); }
  .has-mobile-drawer .site-top-nav-menu a.is-active :deep(.vg-icon) { stroke-width: 2.5; }
  .has-mobile-drawer .site-top-nav-menu a.is-active::after { opacity: 1; transform: translateX(-50%) scaleX(1); }
  .has-mobile-drawer .site-top-nav-actions { display: none; }
  .has-mobile-drawer.is-mobile-minimal .site-top-nav-mobile-search,
  .has-mobile-drawer.is-mobile-minimal .site-top-nav-mobile-notification,
  .has-mobile-drawer.is-mobile-minimal .site-top-nav-drawer { display: none; }
}
</style>
